import React, { useState, useRef, useEffect, useContext } from "react";
import { AppStateContext } from "../../state/AppProvider";

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    placeholder?: string;
    clearOnSend?: boolean;
    showToEnd?: boolean;
    toEnd?: () => void;
    activeCitation?: string;
    windowWidth?: number;
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, showToEnd, toEnd, activeCitation, windowWidth }: Props) => {
    const appStateContext = useContext(AppStateContext);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const calculateRows = (): void => {
        const textArea = textAreaRef.current;
        if (textArea) {
            textArea.style.height = "auto";
            const scrollHeight = textArea.scrollHeight;
            textArea.style.height = `${scrollHeight}px`;
        }
    };

    // Recalculate rows whenever the text changes
    useEffect(() => {
        calculateRows();
    }, [textAreaRef?.current?.value]);

    const [question, setQuestion] = useState<string>("");

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        calculateRows();
        if (!ev.target.value) {
            setQuestion("");
        } else {
            setQuestion(ev.target.value);
        }
    };

    const sendQuestionDisabled = disabled || !question.trim();

    return (
        <div className={"transition-[padding] duration-200 text-base m-auto px-4 " + (appStateContext?.state.isChatHistoryOpen ? "" : "lg:px-16")}>
            {showToEnd && (
                <div
                    className={
                        "absolute left-0 right-0 z-[4] mr-auto w-10 animate-bounce" +
                        (activeCitation && windowWidth && windowWidth >= 1280 ? " ml-[calc(25%_-_20px)]" : " ml-auto")
                    }
                    style={{ bottom: textAreaRef.current ? textAreaRef.current.scrollHeight + 52 : 104 }}
                >
                    <div className="flex cursor-pointer justify-center" onClick={toEnd}>
                        <svg width="32" height="32" viewBox="0 0 40 40" fill="#FFFFFF" xmlns="http://www.w3.org/2000/svg" className="">
                            <rect x="1" y="1" width="38" height="38" rx="19" stroke="#139699" stroke-width="2"></rect>
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M20 22.7092L26.532 16.5L28 17.8954L20 25.5L12 17.8954L13.468 16.5L20 22.7092Z"
                                fill="#139699"
                            ></path>
                        </svg>
                    </div>
                </div>
            )}
            <div className="mx-auto flex flex-1 text-base gap-4 md:gap-6 md:max-w-3xl">
                <div className="flex w-full items-center">
                    <div className="overflow-hidden flex flex-col w-full flex-grow relative rounded-xl bg-j-white shadow-[0px_8px_16px_rgba(0,0,0,0.14),0px_0px_2px_rgba(0,0,0,0.12)]">
                        <textarea
                            ref={textAreaRef}
                            className="m-0 w-full resize-none border-0 bg-transparent focus-visible:outline-0 focus:ring-0 focus-visible:ring-0 py-3.5 pr-[3.75rem] placeholder-j-light-grey max-h-[200px] min-h-[52px] pl-4"
                            placeholder={placeholder}
                            rows={1}
                            value={question}
                            onChange={onQuestionChange}
                            onKeyDown={onEnterPress}
                        ></textarea>
                        <div
                            className={
                                "absolute bottom-0 right-0 h-full w-[52px] rounded-r-xl flex justify-center items-center" +
                                (sendQuestionDisabled
                                    ? " bg-j-super-light-grey cursor-default"
                                    : " bg-gradient-to-r from-j-green to-j-cyan cursor-pointer hover:from-j-green-hover hover:to-j-cyan-hover")
                            }
                        >
                            <a
                                className={
                                    "text-5xl material-symbols-outlined" +
                                    (sendQuestionDisabled ? " text-j-light-grey cursor-default" : " text-j-dark cursor-pointer")
                                }
                                onClick={sendQuestion}
                            >
                                arrow_upward_alt
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            {/* <div className="relative px-2 py-2 text-center text-xs text-j-dark">
                <span className="lg:hidden inline-block">Content provided by Reggie is AI-generated.</span>
                <span className="hidden lg:inline-block">Content provided by Reggie is AI-generated. Consider checking important information.</span>
            </div> */}
        </div>
    );
};
